import React from 'react';
import { useGeneralAssumptions } from './useGeneralAssumptions';
import { HtmlTable } from '../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { formatCurrency, formatPercentage } from '../../../../CommonUtils/formatNumber';
import { MarginProps } from '../../../../theme/styledProps';

export const GeneralAssumptions: React.FC<MarginProps> = props => {
  const { data } = useGeneralAssumptions();

  if (!data) {
    return null;
  }

  return (
    <Box {...props}>
      <HtmlTable>
        <table>
          <colgroup>
            <col width="50%" />
            <col width="50%" />
          </colgroup>

          <caption>General Assumptions</caption>

          <thead>
            <tr>
              <Text as="th" variant="smallCaps" textAlign="left">
                Assumption
              </Text>

              <Text as="th" variant="smallCaps" textAlign="left">
                Value
              </Text>
            </tr>
          </thead>

          <tbody>
            <tr>
              <Text as="td" fontWeight={500}>
                Personal Information
              </Text>

              <Text as="td" fontWeight={500}>
                {data.PersonalInformation.Name}: Year of Birth {data.PersonalInformation.Yob}
              </Text>
            </tr>

            {data.PartnersInformation && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Partner&apos;s Information
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.PartnersInformation.Name}: Year of Birth {data.PartnersInformation.Yob}
                </Text>
              </tr>
            )}

            {data.Children && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Children
                </Text>

                <Box as="td">
                  {data.Children.map(({ Name, Yob }) => (
                    <Text key={Name} fontWeight={500}>
                      {Name}: Year of Birth {Yob}
                    </Text>
                  ))}
                </Box>
              </tr>
            )}

            {data.EmploymentIncome && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Employment Income
                </Text>

                <Box as="td">
                  {data.EmploymentIncome.map(({ Owner, Amount }, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={index} fontWeight={500}>
                      {Owner}: {formatCurrency(Amount)}
                    </Text>
                  ))}
                </Box>
              </tr>
            )}

            {data.BusinessIncome && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Business Income
                </Text>

                <Box as="td">
                  {data.BusinessIncome.map(({ Owner, Amount }, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={index} fontWeight={500}>
                      {Owner}: {formatCurrency(Amount)}
                    </Text>
                  ))}
                </Box>
              </tr>
            )}

            {data.RealEstateIncome?.some(({ Amount }) => Amount > 0) && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Real Estate Income
                </Text>

                <Box as="td">
                  {data.RealEstateIncome.map(({ Owner, Amount }, index) => {
                    if (Amount === 0) {
                      return null;
                    }

                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Text key={index} fontWeight={500}>
                        {Owner}: {formatCurrency(Amount)}
                      </Text>
                    );
                  })}
                </Box>
              </tr>
            )}

            {data.OtherIncome && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Other Income
                </Text>

                <Box as="td">
                  {data.OtherIncome.map(({ Owner, Amount }, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Text key={index} fontWeight={500}>
                      {Owner}: {formatCurrency(Amount)}
                    </Text>
                  ))}
                </Box>
              </tr>
            )}

            {data.EffectiveTaxRate !== null && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Effective Tax Rate
                </Text>

                <Text as="td" fontWeight={500}>
                  {formatPercentage(data.EffectiveTaxRate, { decimalPlaces: 2 })}
                </Text>
              </tr>
            )}

            {data.TaxFilingState && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Tax Filing State
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.TaxFilingState}
                </Text>
              </tr>
            )}

            {data.PreRetirementOverrideState && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Pre-Retirement Override State
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.PreRetirementOverrideState}
                  {data.PreRetirementOverrideEffectiveYear
                    ? ` (effective ${data.PreRetirementOverrideEffectiveYear})`
                    : null}
                </Text>
              </tr>
            )}

            {data.PreRetirementOverrideEffectiveTaxRate !== null && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Pre-Retirement Override Effective Tax Rate
                </Text>

                <Text as="td" fontWeight={500}>
                  {formatPercentage(data.PreRetirementOverrideEffectiveTaxRate, {
                    decimalPlaces: 2,
                  })}
                </Text>
              </tr>
            )}

            <tr>
              <Text as="td" fontWeight={500}>
                Tax Filing Status
              </Text>

              <Text as="td" fontWeight={500}>
                {data.TaxFilingStatus}
              </Text>
            </tr>

            {data.RetirementState && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Retirement State
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.RetirementState}
                </Text>
              </tr>
            )}

            {data.RetirementEffectiveTaxRate !== null && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Retirement Effective Tax Rate
                </Text>

                <Text as="td" fontWeight={500}>
                  {formatPercentage(data.RetirementEffectiveTaxRate, { decimalPlaces: 2 })}
                </Text>
              </tr>
            )}

            <tr>
              <Text as="td" fontWeight={500}>
                Inflation Rate
              </Text>

              <Text as="td" fontWeight={500}>
                {`${Math.round(data.InflationRate * 100 * 100) / 100}%`}
              </Text>
            </tr>

            <tr>
              <Text as="td" fontWeight={500}>
                Current Living Expenses
              </Text>

              <Text as="td" fontWeight={500}>
                {`${formatCurrency(data.CurrentLivingExpenses)} Per Month`}
              </Text>
            </tr>

            {data.RiskToleranceModel !== null && (
              <tr>
                <Text as="td" fontWeight={500}>
                  Risk Tolerance Model
                </Text>

                <Text as="td" fontWeight={500}>
                  {data.RiskToleranceModel}
                </Text>
              </tr>
            )}
          </tbody>
        </table>
      </HtmlTable>
    </Box>
  );
};
